import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { api } from "../../utils/api";
import { readApiErrorMessage } from "../../utils/apiErrorMessage";
import { COLORS } from "../../utils/colors";
import { useRefreshSessionToken } from "../../hooks/useRefreshSessionToken";
import { getCurrentP1Region, getOtherP1Region } from "../../utils/p1Region";
import { showErrorToast, showSuccessToast } from "../../utils/toast";

const MISSING_TOKEN_MESSAGE =
  "Could not get an API token. Refresh the page, or sign out and sign in again if this keeps happening.";

function customerLabel(customer) {
  if (!customer) return "Unknown customer";
  const name = `${customer.firstName || ""} ${customer.lastName || ""}`.trim();
  return name || customer.email || customer._id;
}

function formatMoney(amount, currency) {
  if (amount == null || amount === "") return "—";
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return String(amount);
  const code = (currency || "").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code || "AUD",
    }).format(numeric);
  } catch (_) {
    return `${numeric} ${code}`.trim();
  }
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

const FindAndMergeMemberships = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refreshSessionToken = useRefreshSessionToken();
  const region = getCurrentP1Region();
  const otherRegion = getOtherP1Region(region);

  const ensureApiToken = async () => {
    const ok = await refreshSessionToken();
    if (!ok) {
      showErrorToast(MISSING_TOKEN_MESSAGE);
      return false;
    }
    return true;
  };

  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [targetCustomerId, setTargetCustomerId] = useState(searchParams.get("target") || "");
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [products, setProducts] = useState([]);
  const [linkForm, setLinkForm] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    const loadProducts = async () => {
      if (!(await refreshSessionToken())) return;
      try {
        const response = await api("v1/product?includeAll=true", "GET");
        if (response?.data?.success) {
          setProducts(response.data.data?.allProducts || []);
        }
      } catch (err) {
        console.warn("Failed to load products for Stripe link form", err);
      }
    };
    loadProducts();
  }, [refreshSessionToken]);

  const runSearch = async (term = searchTerm) => {
    const query = String(term || "").trim();
    if (query.length < 2) {
      showErrorToast("Enter at least 2 characters to search.");
      return;
    }
    if (!(await ensureApiToken())) {
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const response = await api("v1/admin/memberships/find", "POST", {
        searchTerm: query,
      });
      if (!response?.data?.success) {
        showErrorToast(readApiErrorMessage(response, "Search failed."));
        setResult(null);
        return;
      }
      const data = response.data.data;
      setResult(data);

      const customerIds = (data.customers || []).map((row) => row.customer?._id).filter(Boolean);
      setTargetCustomerId((current) =>
        current && customerIds.includes(current) ? current : customerIds[0] || current || "",
      );

      const initialVehicles = [];
      for (const row of data.customers || []) {
        for (const item of row.vehicles || []) {
          if (item.vehicle?._id) initialVehicles.push(item.vehicle._id);
        }
      }
      setSelectedVehicleIds(initialVehicles);
    } catch (err) {
      showErrorToast(readApiErrorMessage(err, "Search failed."));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get("q");
    if (initial && initial.trim().length >= 2) {
      runSearch(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleVehicle = (vehicleId, checked) => {
    setSelectedVehicleIds((current) => {
      if (checked) {
        return current.includes(vehicleId) ? current : [...current, vehicleId];
      }
      return current.filter((id) => id !== vehicleId);
    });
  };

  const vehiclesToMerge = useMemo(() => {
    if (!result?.customers || !targetCustomerId) return selectedVehicleIds;
    return selectedVehicleIds.filter((vehicleId) => {
      for (const row of result.customers) {
        for (const item of row.vehicles || []) {
          if (item.vehicle?._id === vehicleId) {
            return String(row.customer?._id) !== String(targetCustomerId);
          }
        }
      }
      return true;
    });
  }, [result, selectedVehicleIds, targetCustomerId]);

  const handlePreview = async () => {
    if (!targetCustomerId) {
      showErrorToast("Select a target customer account.");
      return;
    }
    if (!vehiclesToMerge.length) {
      showErrorToast("Select at least one vehicle that is not already on the target account.");
      return;
    }
    if (!(await ensureApiToken())) {
      return;
    }
    setLoading(true);
    try {
      const response = await api("v1/admin/memberships/merge/preview", "POST", {
        targetCustomerId,
        vehicleIds: vehiclesToMerge,
      });
      if (!response?.data?.success && !response?.data?.data) {
        showErrorToast(readApiErrorMessage(response, "Preview failed."));
        return;
      }
      setPreview(response.data.data);
      setPreviewOpen(true);
      if (response.data.data?.blockers?.length) {
        showErrorToast(readApiErrorMessage(response, "Merge is blocked."));
      }
    } catch (err) {
      showErrorToast(readApiErrorMessage(err, "Preview failed."));
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (!preview?.canExecute) {
      showErrorToast("Resolve preview blockers before merging.");
      return;
    }
    if (!(await ensureApiToken())) {
      return;
    }
    setMerging(true);
    try {
      const response = await api("v1/admin/memberships/merge", "POST", {
        targetCustomerId,
        vehicleIds: vehiclesToMerge,
      });
      if (!response?.data?.success) {
        showErrorToast(readApiErrorMessage(response, "Merge failed."));
        setPreview(response?.data?.data || preview);
        return;
      }
      showSuccessToast(response.data.message);
      setPreviewOpen(false);
      setPreview(response.data.data);
      await runSearch(searchTerm);
    } catch (err) {
      showErrorToast(readApiErrorMessage(err, "Merge failed."));
    } finally {
      setMerging(false);
    }
  };

  const openLinkForm = (stripeSub) => {
    setLinkForm({
      stripeSubscriptionId: stripeSub.stripeSubscriptionId,
      targetCustomerId: targetCustomerId || "",
      rego: stripeSub.suggestedRego && !/^DEFAULT/i.test(stripeSub.suggestedRego)
        ? String(stripeSub.suggestedRego).toUpperCase()
        : "",
      productId: stripeSub.suggestedProductId || "",
      make: stripeSub.matchedSignup?.vehicleMake || "",
      model: stripeSub.matchedSignup?.vehicleModel || "",
      vehicleType: stripeSub.matchedSignup?.vehicleType || "",
    });
  };

  const handleLinkStripe = async () => {
    if (!linkForm?.targetCustomerId) {
      showErrorToast("Select a target customer to link this Stripe subscription.");
      return;
    }
    if (!linkForm.rego) {
      showErrorToast("Enter the vehicle registration.");
      return;
    }
    if (!(await ensureApiToken())) {
      return;
    }
    setLinking(true);
    try {
      const response = await api("v1/admin/memberships/link-stripe", "POST", {
        targetCustomerId: linkForm.targetCustomerId,
        stripeSubscriptionId: linkForm.stripeSubscriptionId,
        rego: linkForm.rego,
        productId: linkForm.productId || undefined,
        make: linkForm.make || undefined,
        model: linkForm.model || undefined,
        vehicleType: linkForm.vehicleType || undefined,
      });
      if (!response?.data?.success) {
        showErrorToast(readApiErrorMessage(response, "Link failed."));
        return;
      }
      showSuccessToast(response.data.message);
      setLinkForm(null);
      await runSearch(searchTerm);
    } catch (err) {
      showErrorToast(readApiErrorMessage(err, "Link failed."));
    } finally {
      setLinking(false);
    }
  };

  const customers = result?.customers || [];
  const selfSignUps = result?.selfSignUps || [];
  const unmatchedStripe = result?.unmatchedStripeSubscriptions || [];

  return (
    <div className="page w-full flex flex-col items-center p-6">
      {(loading || merging || linking) && <GlobalLoadingSpinner />}

      <div className="w-full max-w-6xl">
        <div className="pageTitle flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h1>Find and merge memberships</h1>
          <IconButton onClick={() => navigate("/customer")} sx={{ border: "1px solid grey" }}>
            <ArrowBackIcon />
          </IconButton>
        </div>

        <Alert severity="info" sx={{ mb: 3 }}>
          Searching the <strong>{region}</strong> database only. AU and NZ are separate portals —
          switch to {otherRegion} if the membership was created there. Merging moves vehicles onto
          one customer account and does <strong>not</strong> combine Stripe bills.
        </Alert>

        <Box className="flex flex-col sm:flex-row gap-3 mb-6">
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runSearch();
            }}
            fullWidth
            label="Search email, phone, name, rego, or Stripe id"
            placeholder="name@example.com or ABC123"
          />
          <Button
            variant="contained"
            sx={{ backgroundColor: COLORS.priorityBlue.hex, minWidth: 140 }}
            onClick={() => runSearch()}
          >
            Search
          </Button>
        </Box>

        {result && (
          <>
            <Typography variant="body2" className="mb-4 text-gray-600">
              {result.message || `Results in ${result.region || region}`}
            </Typography>

            <Box className="flex flex-wrap gap-3 mb-6">
              <Button
                variant="outlined"
                onClick={handlePreview}
                disabled={!vehiclesToMerge.length || !targetCustomerId}
              >
                Preview merge ({vehiclesToMerge.length})
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                onClick={handlePreview}
                disabled={!vehiclesToMerge.length || !targetCustomerId}
              >
                Review and merge
              </Button>
            </Box>

            {customers.length === 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                No P1 customers matched this search in {region}. Check self-signups and Stripe
                orphans below, or switch portals.
              </Alert>
            )}

            {Array.isArray(result.unmatchedVehicles) && result.unmatchedVehicles.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {result.unmatchedVehicles.length} vehicle(s) matched but are not linked to a
                customer. Open them from Subscriptions by rego, or link via Stripe if payment exists.
              </Alert>
            )}

            {customers.map((row) => {
              const customer = row.customer;
              const isTarget = String(customer._id) === String(targetCustomerId);
              return (
                <div
                  key={customer._id}
                  className="mb-4 rounded-lg border border-gray-200 bg-white overflow-hidden"
                >
                  <div className="p-4 bg-gray-50 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isTarget}
                            onChange={() => setTargetCustomerId(customer._id)}
                          />
                        }
                        label={
                          <span>
                            <strong>{customerLabel(customer)}</strong>
                            {isTarget ? " — merge target" : ""}
                          </span>
                        }
                      />
                      <div className="text-sm text-gray-600 ml-8">
                        {customer.email} · {customer.phone} · ID {customer._id}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Chip
                        size="small"
                        label={customer.active ? "Active" : "Inactive"}
                        color={customer.active ? "success" : "warning"}
                      />
                      {customer.deleted === "yes" && (
                        <Chip size="small" label="Deleted" color="error" />
                      )}
                      <Button size="small" onClick={() => navigate(`/customer/${customer._id}`)}>
                        Open
                      </Button>
                    </div>
                  </div>

                  <div className="p-4">
                    {(row.vehicles || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No vehicles on this customer.
                      </Typography>
                    )}
                    {(row.vehicles || []).map((item) => {
                      const vehicle = item.vehicle;
                      const checked = selectedVehicleIds.includes(vehicle._id);
                      return (
                        <div
                          key={vehicle._id}
                          className="py-3 border-b last:border-b-0 border-gray-100 flex flex-col md:flex-row md:items-start md:justify-between gap-2"
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={(e) => toggleVehicle(vehicle._id, e.target.checked)}
                              />
                            }
                            label={
                              <span>
                                <strong>{vehicle.rego || "No rego"}</strong>
                                {vehicle.make || (typeof vehicle.model === "string" ? vehicle.model : "")
                                  ? ` · ${vehicle.make || ""} ${
                                      typeof vehicle.model === "string" ? vehicle.model : ""
                                    }`
                                  : ""}
                                {isTarget ? " (already on target)" : ""}
                              </span>
                            }
                          />
                          <div className="md:text-right text-sm text-gray-600">
                            {(item.subscriptions || []).map((sub) => (
                              <div key={sub._id}>
                                {sub.product?.name || "Subscription"} · {sub.active ? "active" : "inactive"}
                                {sub.stripeId ? ` · ${sub.stripeId}` : " · no Stripe id"}
                              </div>
                            ))}
                            {(item.transactions || []).length > 0 && (
                              <div>
                                {item.transactions.length} transaction(s)
                                {item.transactions[0]?.invoiceStatus
                                  ? ` · ${item.transactions[0].invoiceStatus}`
                                  : ""}
                              </div>
                            )}
                            {(!item.subscriptions || item.subscriptions.length === 0) && (
                              <div>No subscription row</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {selfSignUps.length > 0 && (
              <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
                <h2 className="text-lg font-semibold mb-3">Self-signups</h2>
                {selfSignUps.map((signup) => (
                  <div key={signup._id} className="py-2 border-b last:border-b-0 text-sm">
                    <strong>{signup.vehicleRegistration || "No rego"}</strong> · {signup.email} ·{" "}
                    {signup.phone} · {signup.productName} · {signup.status}
                    {signup.customerIdentityResolution
                      ? ` · identity ${signup.customerIdentityResolution}`
                      : ""}
                  </div>
                ))}
              </div>
            )}

            {unmatchedStripe.length > 0 && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <h2 className="text-lg font-semibold mb-2">Stripe subscriptions not in P1</h2>
                <p className="text-sm text-gray-700 mb-3">
                  These Stripe memberships are paid (or exist) but have no matching P1 subscription.
                  Linking creates the vehicle, subscription, transaction, and QR against the existing
                  Stripe id — it does not charge again.
                </p>
                {unmatchedStripe.map((sub) => (
                  <div
                    key={sub.stripeSubscriptionId}
                    className="py-3 border-b last:border-b-0 border-amber-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                  >
                    <div className="text-sm">
                      <div>
                        <strong>{sub.stripeSubscriptionId}</strong> · {sub.status}
                      </div>
                      <div>
                        {sub.stripeCustomerEmail || "No Stripe email"} ·{" "}
                        {sub.amountCents != null
                          ? formatMoney(sub.amountCents / 100, sub.currency)
                          : "—"}
                        {sub.interval ? ` / ${sub.interval}` : ""}
                      </div>
                      <div>
                        Suggested rego: {sub.suggestedRego || "unknown"} · product:{" "}
                        {sub.suggestedProductName || "choose in form"}
                      </div>
                      <div className="text-gray-500">Created {formatDate(sub.created)}</div>
                    </div>
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                      onClick={() => openLinkForm(sub)}
                    >
                      Link to customer
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(result.stripeErrors) && result.stripeErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {result.stripeErrors.join(" ")}
              </Alert>
            )}
          </>
        )}
      </div>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Merge preview</DialogTitle>
        <DialogContent>
          {preview && (
            <div className="flex flex-col gap-3 pt-1">
              <Typography>
                Target: <strong>{customerLabel(preview.target)}</strong> ({preview.target?.email})
              </Typography>
              {(preview.moves || []).map((move) => (
                <div key={move.vehicle?._id} className="text-sm border rounded p-3">
                  Move <strong>{move.vehicle?.rego}</strong> from{" "}
                  {move.fromCustomer ? customerLabel(move.fromCustomer) : "unassigned"} ·{" "}
                  {move.subscriptions?.length || 0} subscription(s), {move.transactionCount}{" "}
                  transaction(s), {move.contractCount} contract(s)
                </div>
              ))}
              {(preview.alreadyOnTarget || []).map((row) => (
                <Alert key={row.vehicleId} severity="info">
                  {row.rego || row.vehicleId}: {row.reason}
                </Alert>
              ))}
              {(preview.blockers || []).map((row) => (
                <Alert key={row.vehicleId} severity="error">
                  {row.message}
                </Alert>
              ))}
              {(preview.sourceCustomerOutcomes || [])
                .filter((row) => row.willSoftDelete)
                .map((row) => (
                  <Alert key={row.customerId} severity="warning">
                    Source customer {row.customerId} will be marked deleted after this merge.
                  </Alert>
                ))}
              <Alert severity="info">
                Stripe subscriptions stay as they are. Three cars still mean three Stripe bills.
              </Alert>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: COLORS.priorityBlue.hex }}
            onClick={handleMerge}
            disabled={!preview?.canExecute || merging}
          >
            Confirm merge
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!linkForm} onClose={() => setLinkForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Stripe subscription</DialogTitle>
        <DialogContent>
          {linkForm && (
            <div className="flex flex-col gap-3 pt-2">
              <Typography variant="body2">{linkForm.stripeSubscriptionId}</Typography>
              <TextField
                select
                SelectProps={{ native: true }}
                label="Target customer"
                value={linkForm.targetCustomerId}
                onChange={(e) =>
                  setLinkForm((current) => ({ ...current, targetCustomerId: e.target.value }))
                }
              >
                <option value="">Select customer</option>
                {customers.map((row) => (
                  <option key={row.customer._id} value={row.customer._id}>
                    {customerLabel(row.customer)} ({row.customer.email})
                  </option>
                ))}
              </TextField>
              <TextField
                label="Vehicle registration"
                value={linkForm.rego}
                onChange={(e) =>
                  setLinkForm((current) => ({
                    ...current,
                    rego: String(e.target.value || "").toUpperCase(),
                  }))
                }
              />
              <TextField
                select
                SelectProps={{ native: true }}
                label="Product"
                value={linkForm.productId}
                onChange={(e) =>
                  setLinkForm((current) => ({ ...current, productId: e.target.value }))
                }
              >
                <option value="">Auto-detect if possible</option>
                {products
                  .filter((product) => product.type === "subscription" || !product.isAddOnOnly)
                  .map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
              </TextField>
              <TextField
                label="Make (optional)"
                value={linkForm.make}
                onChange={(e) => setLinkForm((current) => ({ ...current, make: e.target.value }))}
              />
              <TextField
                label="Model (optional)"
                value={linkForm.model}
                onChange={(e) => setLinkForm((current) => ({ ...current, model: e.target.value }))}
              />
              <Alert severity="warning">
                This attaches the existing Stripe membership. It will not create a new Stripe
                subscription or charge the customer again.
              </Alert>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkForm(null)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: COLORS.priorityBlue.hex }}
            onClick={handleLinkStripe}
            disabled={linking}
          >
            Link membership
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FindAndMergeMemberships;
